<template>
  <div
    :class="{ 'lzd-date-picker': true, 'el-input': formItem }"
    ref="datePickerParentRef"
  >
    <div
      :class="{
        'lzd-date-picker-value': true,
        'el-input__wrapper': formItem,
        readonly
      }"
      style="line-height: 1"
      v-text="dateStr"
      :placeholder="placeholder"
      lzd-date-picker-value
      @click="openDatePicker"
    ></div>
    <el-date-picker
      v-model="dateValue"
      ref="datePickerRef"
      v-bind="attrs"
      :value-format="format"
      :format="format"
      :type="type"
      :size="size"
      :readonly="readonly"
      :show-now="false"
      :show-footer="false"
      :show-confirm="false"
      :disabled-date="disabledDate"
      @change="updateModelValue"
      style="opacity: 0; width: 100%; overflow: hidden"
    />
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed, toRaw, useAttrs, watch, onMounted } from 'vue';
  import { isEqual } from 'lodash-es';
  import { toI18n } from '@/api/bsl/system/i18n';
  import {
    date,
    dateFormat,
    dateNow,
    nowMonthRange,
    nowWeekRange,
    parseDate
  } from '@/utils/lzd-date-util';

  /** 属性 */
  const props = withDefaults(
    defineProps<{
      /** v-model */
      modelValue?: string | number | Date;
      placeholder?: string;
      readonly?: boolean;
      size?: '' | 'large' | 'default' | 'small';
      /** 'YYYY-MM-DD' | 'YYYY/MM/DD' | 'YYYY年MM月DD日' | 'YYYY-MM-DD HH:mm:ss' | 'YYYY/MM/DD HH:mm:ss' | 'YYYY年MM月DD日 HH时mm分ss秒' */
      format?: string;
      type?:
        | 'year'
        | 'years'
        | 'month'
        | 'months'
        | 'date'
        | 'dates'
        | 'datetime'
        | 'week'
        | 'datetimerange'
        | 'daterange'
        | 'monthrange'
        | 'yearrange';
      rangeSeparator?: string;
      /** 隐藏清除按钮 */
      hideClearBtn?: boolean;
      /** 值范围：week本周、month本月、nowBefore现在之前、nowAtfer现在之后 */
      valueRange?: 'week' | 'month' | 'nowBefore' | 'nowAtfer';
      min?: Date | string | number;
      max?: Date | string | number;
      formItem?: boolean;
      /** 填充当前时间 */
      dateFill?: boolean;
      /** 条件类型 */
      dateDiffType?: 'lt' | 'le' | 'gt' | 'gt';
      /** 条件值 */
      dateDiffValue?: Date | string | number;
    }>(),
    {
      format: 'YYYY-MM-DD',
      type: 'date',
      size: 'default',
      placeholder: '',
      readonly: false,
      rangeSeparator: ' ~ ',
      hideClearBtn: false,
      dateFill: false
    }
  );

  /** 事件 */
  const emit = defineEmits<{
    /** 修改值 */
    (e: 'update:modelValue', value: string): void;
  }>();

  const attrs = useAttrs();
  const dateValue = ref<string | number | Date>(props.modelValue ?? '');
  const datePickerParentRef = ref<HTMLDivElement>();
  const datePickerRef = ref();
  const updateModelValue = () => {
    emit('update:modelValue', dateStr.value);
  };

  const dateStr = computed(function () {
    const currDateValue = toRaw(dateValue.value);
    if (Array.isArray(currDateValue)) {
      if (currDateValue.length === 2 && currDateValue[0]) {
        return currDateValue.join(props.rangeSeparator);
      }
      return currDateValue[0];
    }
    return currDateValue;
  });

  let diffMin: Date | null = null;
  let diffMax: Date | null = null;
  let diffNow: Date | null = null;
  let diffErr = false;
  let diffValue: Date | null = null;
  const openDatePicker = () => {
    if (!props.readonly) {
      if (props.min || props.max || props.valueRange) {
        diffNow = date();
        diffErr = false;
      }
      try {
        let id = datePickerParentRef.value
          ?.querySelector('input[aria-controls]')
          ?.getAttribute('aria-controls');
        if (id) {
          const datePickerEl = document.querySelector(
            '#' + id
          ) as HTMLDivElement;
          if (!datePickerEl?.querySelector('.el-picker-panel__footer')) {
            const __datePicker__body_wrapper = datePickerEl.querySelector(
              '.el-picker-panel__body-wrapper'
            ) as HTMLDivElement;
            const __datePicker__footer_wrapper = document.createElement('div');
            __datePicker__footer_wrapper.classList.add(
              'el-picker-panel__footer'
            );
            __datePicker__footer_wrapper.innerHTML = `
            <button aria-disabled="false" type="button" class="el-button el-button--small is-text el-picker-panel__link-btn lzd-date-btn-clear"><span >${toI18n('清除')}</span></button>
            <button aria-disabled="false" type="button" class="el-button el-button--small is-text el-picker-panel__link-btn lzd-date-btn-now"><span >${toI18n('现在')}</span></button>
            <button aria-disabled="false" type="button" class="el-button el-button--small is-plain el-picker-panel__link-btn lzd-date-btn-confirm"><span>${toI18n('确认')}</span></button>
            `;
            __datePicker__body_wrapper.parentNode?.appendChild(
              __datePicker__footer_wrapper
            );

            __datePicker__footer_wrapper
              .querySelectorAll('button')
              .forEach((btn, i) => {
                let btn_index = i;
                if (i === 0 && props.hideClearBtn) {
                  btn.style.display = 'none';
                }
                if (btn_index === 1) {
                  if (
                    props.type?.includes('range') ||
                    props.valueRange ||
                    disabledDate(new Date())
                  ) {
                    btn.style.display = 'none';
                  } else {
                    btn.style.display = 'block';
                  }
                }
                btn.addEventListener('click', () => {
                  if (btn_index === 0) {
                    if (props.hideClearBtn) {
                      return;
                    }
                    dateValue.value = '';
                  } else if (btn_index === 1) {
                    const now = new Date();
                    if (!disabledDate(now)) {
                      dateValue.value = dateFormat(now, props.format);
                    } else {
                      console.warn('当前时间不在可选范围内');
                    }
                  } else if (btn_index === 2) {
                    datePickerRef.value?.handleClose();
                  }
                });
              });
          }

          // 再时间内
          // if (isDiff(dateNow()) && !props.type?.includes('range')) {
          //   const btn = datePickerEl.querySelector(
          //     '.lzd-date-btn-now'
          //   ) as HTMLButtonElement;
          //   btn && (btn.style.display = 'block');
          // }
        }
      } catch (err) {
        console.error(err);
      }
      datePickerRef.value?.handleOpen();
    }
  };

  function isDiff(time: number) {
    if (props.dateDiffType && props.dateDiffValue) {
      diffValue = diffValue ?? parseDate(props.dateDiffValue);
      if (!diffValue) {
        return true;
      }
      if (props.dateDiffType === 'lt') {
        return time < diffValue.getTime();
      }
      if (props.dateDiffType === 'le') {
        return time < diffValue.getTime();
      }
      if (props.dateDiffType === 'gt') {
        return time > diffValue.getTime();
      }
      if (props.dateDiffType === 'ge') {
        return time >= diffValue.getTime();
      }
    }
    return true;
  }

  /** 禁止选择的日期 */
  const disabledDate = (curr: Date) => {
    const time = curr.getTime();

    // 不满足
    if (!isDiff(time)) {
      return true;
    }
    if (diffErr || (!props.min && !props.max && !props.valueRange)) {
      return false;
    }

    try {
      // 配置了最小和最大
      if (props.min && props.max) {
        diffMin = diffMin ?? parseDate(props.min);
        diffMax = diffMax ?? parseDate(props.max);
        if (!diffMin || !diffMax) {
          return false;
        }
        return (
          time < diffMin.setHours(0, 0, 0, 0) ||
          time > diffMax.setHours(0, 0, 0, 0)
        );
      }

      // 最小值
      if (props.min) {
        diffMin = diffMin ?? parseDate(props.min);
        if (!diffMin) {
          return false;
        }
        return time < diffMin.setHours(0, 0, 0, 0);
      }
      // 最大值
      if (props.max) {
        diffMax = diffMax ?? parseDate(props.max);
        if (!diffMax) {
          return false;
        }
        return time > diffMax.setHours(0, 0, 0, 0);
      }

      if (!props.valueRange) {
        return false;
      }
      diffNow = diffNow ?? date();
      // 本周
      if (props.valueRange === 'week') {
        const { start, end } = nowWeekRange(diffNow);
        return time < start.getTime() || time > end.getTime();
      }
      // 本月
      else if (props.valueRange === 'month') {
        const { start, end } = nowMonthRange(diffNow);
        return time < start.getTime() || time > end.getTime();
      }
      // 现在之前
      else if (props.valueRange === 'nowBefore') {
        return time > new Date(diffNow).setHours(0, 0, 0, 0);
      }
      // 现在之后
      else if (props.valueRange === 'nowAtfer') {
        return time < new Date(diffNow).setHours(0, 0, 0, 0);
      }
    } catch (err) {
      diffErr = true;
      console.error(err);
    }
    return false;
  };

  watch(
    () => props.modelValue,
    (newVal) => {
      if (!isEqual(newVal, dateValue.value)) {
        dateValue.value = newVal ?? '';
      }
    },
    { immediate: true }
  );

  onMounted(() => {
    if (props.dateFill && props.type.endsWith('range')) {
      dateValue.value = dateFormat(date(), props.format);
    }
  });
</script>

<script lang="ts">
  export default {
    name: 'LzdDatePicker'
  };
</script>
<style lang="scss" scoped></style>
